import { apiGet, apiPost, apiPut, resolveApiUrl } from "./client";
import type {
  AdminCollectionItem,
  AdminCollectionListPayload,
  AdminFollowRequestItem,
  AdminFollowRequestsListPayload,
  AdminFollowsListPayload,
  AdminFriendshipSummaryPayload,
  AdminLoginPayload,
  AdminOperationsOverview,
  AdminPlaceDetails,
  AdminShareItem,
  AdminShareListPayload,
  AdminUserContentPayload,
  AdminUserProfile,
  AdminWcoBulkUpdatePayload,
  AdminWcoDuplicateCleanupPayload,
  AdminWcoDuplicatePreviewPayload,
  AdminWcoItem,
  AdminWcoListPayload,
  BaseResponse,
  MigrationUserListPayload
} from "../../shared/types/admin";

export function normalizeResponseData<T>(response: BaseResponse<T>): T | undefined {
  return response.data;
}

export async function loginAdmin(email: string, password: string) {
  return apiPost<AdminLoginPayload, { email: string; password: string }>(
    "/v1/admin/login",
    { email, password }
  );
}

export async function loadAdminUsers(
  token: string,
  params: {
    query?: string;
    limit?: number;
    activeDays?: number;
    inactiveDays?: number;
    sort?: string;
    onlyIncomplete?: boolean;
    onlyNoFriends?: boolean;
    onlyNoLists?: boolean;
  }
) {
  return apiGet<MigrationUserListPayload>("/v1/admin/users/list", token, params);
}

export async function listAdminWco(
  token: string,
  params: { page: number; size: number; query?: string }
) {
  return apiGet<AdminWcoListPayload>("/v1/admin/wco", token, params);
}

export async function updateAdminWco(
  token: string,
  placeListId: string,
  payload: { userComment: string; placeType: string[] }
) {
  return apiPut<AdminWcoItem, { userComment: string; placeType: string[] }>(
    `/v1/admin/wco/${encodeURIComponent(placeListId)}`,
    payload,
    token
  );
}

export async function updateAdminWcoBulk(
  token: string,
  payload: { placeListIds: string[]; userComment?: string; placeType?: string[] }
) {
  return apiPost<AdminWcoBulkUpdatePayload, { placeListIds: string[]; userComment?: string; placeType?: string[] }>(
    "/v1/admin/wco/bulk",
    payload,
    token
  );
}

export async function listAdminWcoDuplicates(
  token: string,
  params: { page: number; size: number; listType?: string }
) {
  return apiGet<AdminWcoDuplicatePreviewPayload>("/v1/admin/wco/duplicates", token, params);
}

export async function cleanupAdminWcoDuplicates(
  token: string,
  payload: { dryRun?: boolean; maxGroups?: number; listType?: string }
) {
  return apiPost<AdminWcoDuplicateCleanupPayload, { dryRun?: boolean; maxGroups?: number; listType?: string }>(
    "/v1/admin/wco/duplicates/cleanup",
    payload,
    token
  );
}

export async function markAdminWcoPlaceClosed(token: string, placeId: string) {
  return apiPost<AdminPlaceDetails, undefined>(
    `/v1/admin/wco/places/${encodeURIComponent(placeId)}/close`,
    undefined,
    token
  );
}

export async function listAdminShares(
  token: string,
  params: {
    page: number;
    size: number;
    query?: string;
    shareType?: "COLLECTION" | "WECOMMENDATION";
    onlyExpired?: boolean;
  }
) {
  return apiGet<AdminShareListPayload>("/v1/admin/shares", token, params);
}

export async function updateAdminShare(
  token: string,
  shareId: string,
  payload: { invalidAt?: string | null; disable?: boolean }
) {
  return apiPut<AdminShareItem, { invalidAt?: string | null; disable?: boolean }>(
    `/v1/admin/shares/${encodeURIComponent(shareId)}`,
    payload,
    token
  );
}

export async function listAdminCollections(
  token: string,
  params: {
    page: number;
    size: number;
    query?: string;
    type?: "WCO" | "BUCKETLIST";
    userId?: string;
  }
) {
  return apiGet<AdminCollectionListPayload>("/v1/admin/collections", token, params);
}

export async function updateAdminCollection(
  token: string,
  collectionId: string,
  payload: {
    title?: string;
    coverImage?: string | null;
    type?: "WCO" | "BUCKETLIST";
    placeIds?: string[];
  }
) {
  return apiPut<AdminCollectionItem, {
    title?: string;
    coverImage?: string | null;
    type?: "WCO" | "BUCKETLIST";
    placeIds?: string[];
  }>(
    `/v1/admin/collections/${encodeURIComponent(collectionId)}`,
    payload,
    token
  );
}

export async function getPlaceDetails(token: string, placeId: string, forced = false) {
  const params = new URLSearchParams();
  params.set("forced", String(forced));
  params.set("imageWidth", "640");
  params.set("imageCount", "5");

  const path = `/v1/locations/${encodeURIComponent(placeId)}?${params.toString()}`;
  const response = await fetch(resolveApiUrl(path), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    throw new Error(
      typeof parsed === "string"
        ? parsed
        : `GET ${path} failed (HTTP ${response.status})`
    );
  }

  return parsed as AdminPlaceDetails;
}

export async function getAdminUserProfile(token: string, userId: string) {
  return apiGet<AdminUserProfile>(`/v1/admin/users/${encodeURIComponent(userId)}`, token);
}

export async function updateAdminUserProfile(
  token: string,
  userId: string,
  payload: {
    name?: string;
    avatar?: string;
    bio?: string;
    city?: string;
    email?: string;
    website?: string;
    wallpaper?: string;
    push?: boolean;
    sticker?: boolean;
    autoAcceptFollower?: boolean;
    flags?: string[];
    roles?: string[];
  }
) {
  return apiPut<AdminUserProfile, typeof payload>(
    `/v1/admin/users/${encodeURIComponent(userId)}`,
    payload,
    token
  );
}

export async function getAdminUserContent(
  token: string,
  userId: string,
  wcoPage: number,
  bucketPage: number,
  size: number
) {
  return apiGet<AdminUserContentPayload>(`/v1/admin/users/${encodeURIComponent(userId)}/content`, token, {
    wcoPage,
    bucketPage,
    size
  });
}

export async function getAdminOperationsOverview(
  token: string,
  params: { days?: number; limit?: number }
) {
  return apiGet<AdminOperationsOverview>("/v1/admin/operations/overview", token, params);
}

export async function getAdminFriendshipSummary(token: string, userId: string) {
  return apiGet<AdminFriendshipSummaryPayload>("/v1/admin/friendships/summary", token, { userId });
}

export async function listAdminFollows(
  token: string,
  params: { userId?: string; query?: string; page?: number; size?: number }
) {
  return apiGet<AdminFollowsListPayload>("/v1/admin/friendships/follows", token, params);
}

export async function listAdminFollowRequests(
  token: string,
  params: { userId?: string; query?: string; status?: string; page?: number; size?: number }
) {
  return apiGet<AdminFollowRequestsListPayload>("/v1/admin/friendships/requests", token, params);
}

export async function deleteAdminFollow(token: string, followId: string) {
  const response = await fetch(resolveApiUrl(`/v1/admin/friendships/follows/${encodeURIComponent(followId)}`), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `DELETE follow failed (HTTP ${response.status})`);
  }
}

export async function updateAdminFollowRequestStatus(
  token: string,
  requestId: string,
  status: "PENDING" | "ACCEPTED" | "REJECTED" | "WITHDRAWN"
) {
  return apiPut<AdminFollowRequestItem, undefined>(
    `/v1/admin/friendships/requests/${encodeURIComponent(requestId)}/status?status=${status}`,
    undefined,
    token
  );
}

export function apiErrorMessage(error: unknown): string {
  if (!error) {
    return "Unbekannter Fehler";
  }
  const apiError = error as { status?: number; body?: { error?: unknown; message?: unknown } };
  const body = apiError.body;
  if (body?.error) {
    return String(body.error);
  }
  if (body?.message) {
    return String(body.message);
  }
  if (apiError.status) {
    return `HTTP ${apiError.status}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
